import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  onSnapshot,
  Unsubscribe,
  QueryConstraint,
  DocumentData,
  CollectionReference,
  DocumentReference,
} from 'firebase/firestore';
import { db } from '../../lib/firestore';
import { devError } from '../../utils/dev-log';

/**
 * Base Firestore service with common CRUD operations and real-time listeners
 * Provides clean separation of concerns and type-safe data access
 */
export abstract class BaseFirestoreService<T extends DocumentData> {
  protected collectionName: string;

  constructor(collectionName: string) {
    this.collectionName = collectionName;
  }

  /**
   * Get collection reference
   */
  protected getCollectionRef(): CollectionReference<T> {
    return collection(db, this.collectionName) as CollectionReference<T>;
  }

  /**
   * Get document reference
   */
  protected getDocRef(id: string): DocumentReference<T> {
    return doc(db, this.collectionName, id) as DocumentReference<T>;
  }

  /**
   * Get a single document by ID
   */
  async getById(id: string): Promise<T | null> {
    const docRef = this.getDocRef(id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }
    
    return { id: docSnap.id, ...docSnap.data() } as T;
  }

  /**
   * Get all documents in collection
   */
  async getAll(constraints: QueryConstraint[] = []): Promise<T[]> {
    const collectionRef = this.getCollectionRef();
    const q = query(collectionRef, ...constraints);
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as T));
  }

  /**
   * Subscribe to a single document with real-time updates
   */
  subscribeToDocument(
    id: string,
    onUpdate: (data: T | null) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    const docRef = this.getDocRef(id);
    
    return onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          onUpdate({ id: docSnap.id, ...docSnap.data() } as T);
        } else {
          onUpdate(null);
        }
      },
      (error) => {
        devError(`Error subscribing to document ${id}:`, error);
        onError?.(error);
      }
    );
  }

  /**
   * Subscribe to collection with real-time updates
   */
  subscribeToCollection(
    onUpdate: (data: T[]) => void,
    onError?: (error: Error) => void,
    constraints: QueryConstraint[] = []
  ): Unsubscribe {
    const collectionRef = this.getCollectionRef();
    const q = query(collectionRef, ...constraints);
    
    return onSnapshot(
      q,
      (querySnapshot) => {
        const data = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        } as T));
        onUpdate(data);
      },
      (error) => {
        devError(`Error subscribing to collection ${this.collectionName}:`, error);
        onError?.(error);
      }
    );
  }

  /**
   * Query documents with filters
   */
  async query(constraints: QueryConstraint[]): Promise<T[]> {
    return this.getAll(constraints);
  }
}
